import { useEffect, useState } from 'react';

export type NetworkQuality = 'good' | 'slow' | 'offline';
export type ConnectionType = 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';

interface NetworkQualityState {
  isOnline: boolean;
  connectionType: ConnectionType;
  quality: NetworkQuality;
  isPoorQuality: boolean;
}

/**
 * Hook to monitor network quality using browser APIs
 * - Monitors navigator.onLine for offline/online status
 * - Uses NetworkInformation API for connection speed (Chrome/Edge only)
 * - Returns current network quality state
 */
export const useNetworkQuality = (): NetworkQualityState => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [connectionType, setConnectionType] = useState<ConnectionType>('unknown');

  useEffect(() => {
    // Update online/offline status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Monitor connection type using NetworkInformation API (Chrome/Edge only)

    const connection =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (connection) {
      const updateConnectionType = () => {
        const effectiveType = connection.effectiveType as ConnectionType;
        setConnectionType(effectiveType || 'unknown');
      };

      // Initial check
      updateConnectionType();

      // Listen for changes
      connection.addEventListener('change', updateConnectionType);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        connection.removeEventListener('change', updateConnectionType);
      };
    }

    // Cleanup if NetworkInformation API not available
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Determine overall quality
  const quality: NetworkQuality = !isOnline
    ? 'offline'
    : connectionType === 'slow-2g' || connectionType === '2g'
      ? 'slow'
      : 'good';

  const isPoorQuality = quality !== 'good' && isOnline;

  return {
    isOnline,
    connectionType,
    quality,
    isPoorQuality,
  };
};
